<template>
  <v-modal
    id="email-summary-modal"
    class-name="modal-fullscreen-sm-down"
    size="lg"
    @closed="$emit('closed')"
  >
    <modal-header>
      <modal-title>{{ title }}</modal-title>
    </modal-header>

    <modal-body>
      <div v-if="warningText || warningResources.length" class="alert alert-warning mb-16" role="alert">
        <div v-if="warningText" class="mb-8">{{ warningText }}</div>
        <ul v-if="warningResources.length" class="mb-0 ps-16 mt-8">
          <li v-for="(item, i) in warningResources" :key="i" class="text-sm">
            <span class="fw-medium">{{ item.name }}</span>
            <span v-if="item.date" class="text-muted"> — {{ item.date }}</span>
          </li>
        </ul>
      </div>

      <p class="fw-semi text-black mb-16">{{ $t("tourBooking.emailModal.summaryText") }}</p>

      <div class="email-summary-scroll">
        <div v-if="suppliers.length === 0" class="text-center text-muted py-24">
          {{ $t("tourBooking.noResourcesSelectedMessage") }}
        </div>
        <div
          v-for="(supplier, sIndex) in suppliers"
          :key="sIndex"
          class="email-summary-supplier"
        >
          <div class="email-summary-supplier-name">
            <v-icon icon="mail" size="sm" space="me-8" />
            <span class="fw-semi">{{ supplier.name }}</span>
          </div>
          <div class="email-summary-groups">
            <div
              v-for="(group, gIndex) in supplier.itemResourcesGrouped || []"
              :key="gIndex"
              class="email-summary-group"
            >
              <div class="email-summary-group-name fw-medium">
                {{ group.name ?? 'Unknown' }}
              </div>
              <div class="email-summary-group-details d-flex flex-wrap gap-12">
                <span class="badge bg-light text-dark">
                  {{ $t("tourBooking.quantity") }}: {{ group.quantity }}
                </span>
                <span class="badge bg-light text-dark">
                  {{ $t("general.shared.checkIn") }}: {{ formatDate(group.date) }}
                </span>
                <span v-if="getNights(group) > 0" class="badge bg-light text-dark">
                  {{ $t("tourPricing.nights") }}: {{ getNights(group) }}
                </span>
                <span v-if="getPaxCount(group) > 0" class="badge bg-light text-dark">
                  {{ $t("tourBooking.pax") }}: {{ getPaxCount(group) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal-body>

    <modal-footer>
      <v-button
        class="btn-tertiary"
        size="sm"
        @click="$emit('closed')"
      >
        {{ $t("general.button.cancel") }}
      </v-button>
      <v-button
        class="btn-primary cursor-pointer"
        size="sm"
        @click="$emit('confirm')"
      >
        {{ $t("general.button.send") }}
      </v-button>
    </modal-footer>
  </v-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { DateTime } from "luxon";

export default defineComponent({
  name: "TheEmailSummaryModal",
  components: {
    VModal,
    ModalHeader,
    ModalTitle,
    ModalBody,
    ModalFooter,
    VButton,
    VIcon,
  },
  emits: ["closed", "confirm"],
  props: {
    title: {
      type: String,
      default: "",
    },
    warningText: {
      type: String,
      default: "",
    },
    warningResources: {
      type: Array as () => Array<{ name: string; date: string }>,
      default: () => [],
    },
    suppliers: {
      type: Array as () => any[],
      default: () => [],
    },
    formatDateStr: {
      type: String,
      default: "dd/MM/yyyy",
    },
  },
  methods: {
    formatDate(date: string | null): string {
      if (!date) return "-";
      try {
        return DateTime.fromISO(date).toFormat(this.formatDateStr);
      } catch {
        return date;
      }
    },
    getNights(group: any): number {
      if (!group.date || !group.endDate) return 0;
      try {
        const init = DateTime.fromISO(group.date).startOf("day");
        const end = DateTime.fromISO(group.endDate).startOf("day");
        return end.diff(init, "days").days;
      } catch {
        return 0;
      }
    },
    getPaxCount(group: any): number {
      // Prefer group-level paxes array (array of names from pricing page)
      if (Array.isArray(group.paxes) && group.paxes.length > 0) {
        return group.paxes.length;
      }
      // Fallback: sum resource-level paxes
      return group.resources?.reduce((sum: number, r: any) => sum + (r.paxes?.length ?? 0), 0) ?? 0;
    },
  },
});
</script>

<style scoped>
.email-summary-scroll {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}

.email-summary-supplier {
  margin-bottom: 20px;
}

.email-summary-supplier:last-child {
  margin-bottom: 0;
}

.email-summary-supplier-name {
  display: flex;
  align-items: center;
  font-size: 1rem;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

.email-summary-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.email-summary-group {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.email-summary-group-name {
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.email-summary-group-details {
  font-size: 0.85rem;
}

.email-summary-group-details .badge {
  font-weight: 500;
  padding: 4px 10px;
}
</style>
